import {FC, useMemo} from 'react';
import {Form, Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import Question from './Question';
import {Button} from '../../../../../common/Button';
import {formatFormFields} from './helpers/formatFormFields';
import {generateValidationSchema} from './helpers/generateValidationSchema';
import {getInitialValues} from './helpers/getInitialValues';
import {CurrentTaskAnswer, TaskFormField} from '../../../../types';
import EnsurePermission from '../../../../../common/EnsurePermission';
import permissionsInfo from '../../../../../../constants/permissionsInfo';
import {meetsVisibilityCriteria} from './helpers/meetsVisibilityCriteria';
import {allRequiredFieldsHaveInitialValues} from './helpers/allRequiredFieldsHaveInitialValues';

type QuestionnaireProps = {
  onSubmit: (values: Dictionary<string | string[]>) => void,
  formFields: TaskFormField[],
  answers: CurrentTaskAnswer[],
  isCurrentUserAssignee: boolean,
  isChecklistCompleted: boolean,
  isRunning: boolean,
  isSubmitting: boolean,
}

const Questionnaire: FC<QuestionnaireProps> = ({
  onSubmit,
  answers,
  formFields,
  isCurrentUserAssignee,
  isChecklistCompleted,
  isRunning,
  isSubmitting
}) => {
  const {t} = useTranslation('common');

  const {
    formattedFormFields,
    initialValues,
    validationSchema,
    areAllRequiredFieldsHaveInitialValues
  } = useMemo(() => {
    const formattedFormFields = formatFormFields(formFields);
    const validationSchema = generateValidationSchema(formattedFormFields);
    const initialValues = getInitialValues(formattedFormFields, answers);
    const areAllRequiredFieldsHaveInitialValues =
      allRequiredFieldsHaveInitialValues(formFields, initialValues);

    return {
      formattedFormFields,
      validationSchema,
      initialValues,
      areAllRequiredFieldsHaveInitialValues
    };
  },
  [formFields, answers]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {({isValid, values, dirty}) => {
        const isDisabledButton = [
          !isChecklistCompleted,
          !isRunning,
          !isValid,
          isSubmitting,
          ...(areAllRequiredFieldsHaveInitialValues ? [] : [!dirty])
        ].some((value) => value);

        const isDisabledInput = !isCurrentUserAssignee ||
          !isRunning ||
          isSubmitting;

        return (
          <Form className="form task-questionnaire" autoComplete="off">
            <div className="details__questionnaire">
              {formattedFormFields.filter(({required, conditions}) =>
                meetsVisibilityCriteria(required, conditions, values))
                  .map((field) => (
                    <Question
                      key={`${field.id}`}
                      field={field}
                      disabled={isDisabledInput}
                    />
                  ))}
            </div>

            {isCurrentUserAssignee && isRunning && (
              <EnsurePermission
                elementPermissions={permissionsInfo.tasks.complete}
              >
                <div className="current-task__button-container">
                  <Button
                    label={t('complete')}
                    type="submit"
                    disabled={isDisabledButton}
                  />
                </div>
              </EnsurePermission>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default Questionnaire;
